import { ArrowRight, Building2, ShieldCheck } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import type { AppSettings, CompanyProfile } from '../types';

interface OnboardingScreenProps {
  email: string;
  settings: AppSettings;
  requiresConsent: boolean;
  onComplete: (settings: AppSettings) => Promise<void>;
  onSignOut: () => void;
}

export function OnboardingScreen({ email, settings, requiresConsent, onComplete, onSignOut }: OnboardingScreenProps) {
  const [company, setCompany] = useState<CompanyProfile>({ ...settings.company, email: settings.company.email || email });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [acceptedPolicies, setAcceptedPolicies] = useState(!requiresConsent);
  const patch = (key: keyof CompanyProfile, value: string) => setCompany((current) => ({ ...current, [key]: value }));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!company.name.trim()) { setError('กรุณากรอกชื่อบริษัทหรือชื่อผู้ขาย'); return; }
    if (!acceptedPolicies) { setError('กรุณายอมรับเงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัว'); return; }
    setBusy(true);
    setError('');
    try {
      await onComplete({ ...settings, company: { ...company, name: company.name.trim() } });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'บันทึกข้อมูลไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  const skip = async () => {
    if (!acceptedPolicies) { setError('กรุณายอมรับเงื่อนไขการใช้งานและนโยบายความเป็นส่วนตัวก่อนเริ่มใช้งาน'); return; }
    setBusy(true);
    setError('');
    try {
      await onComplete({ ...settings, company });
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'เริ่มใช้งานไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  };

  return <main className="onboarding-shell">
    <header className="onboarding-header"><div className="auth-brand"><span>BF</span><strong>BilaFlow</strong></div><button className="btn ghost small" onClick={onSignOut}>ออกจากระบบ</button></header>
    <section className="onboarding-card">
      <div className="onboarding-intro"><div className="onboarding-icon"><Building2 size={27} /></div><span className="eyebrow">ตั้งค่าครั้งแรก</span><h1>ข้อมูลสำหรับหัวเอกสาร</h1><p>กรอกตอนนี้หรือข้ามไปก่อน แล้วกลับมาเพิ่มภายหลังได้ที่หน้าตั้งค่า</p></div>
      <form onSubmit={submit}>
        <div className="field-grid two"><label><span>ประเภทผู้ขาย</span><select value={company.type} onChange={(event) => patch('type', event.target.value)}><option value="company">นิติบุคคล / บริษัท</option><option value="individual">บุคคลธรรมดา</option></select></label><label><span>ชื่อบริษัท / ชื่อผู้ขาย</span><input value={company.name === 'บริษัทของคุณ' ? '' : company.name} onChange={(event) => patch('name', event.target.value)} required autoFocus /></label></div>
        <div className="field-grid two"><label><span>ชื่อผู้ติดต่อ</span><input value={company.contactName} onChange={(event) => patch('contactName', event.target.value)} /></label><label><span>อีเมล</span><input type="email" value={company.email} onChange={(event) => patch('email', event.target.value)} /></label></div>
        <div className="field-grid two"><label><span>เบอร์โทรศัพท์</span><input value={company.phone} onChange={(event) => patch('phone', event.target.value)} /></label><label><span>เลขประจำตัวผู้เสียภาษี (ถ้ามี)</span><input inputMode="numeric" value={company.taxId} onChange={(event) => patch('taxId', event.target.value.replace(/\D/g, '').slice(0, 13))} /></label></div>
        <label><span>ที่อยู่สำหรับออกเอกสาร</span><textarea rows={3} value={company.address} onChange={(event) => patch('address', event.target.value)} /></label>
        {requiresConsent && <label className="auth-consent"><input type="checkbox" checked={acceptedPolicies} onChange={(event) => setAcceptedPolicies(event.target.checked)} /><span>ฉันยอมรับ <a href="/terms.html" target="_blank" rel="noreferrer">เงื่อนไขการใช้งาน</a> และ <a href="/privacy.html" target="_blank" rel="noreferrer">นโยบายความเป็นส่วนตัว</a></span></label>}
        {error && <p className="auth-error" role="alert">{error}</p>}
        <div className="onboarding-note"><ShieldCheck size={17} /><span>แพ็กเกจ Free เก็บข้อมูลนี้ไว้ในอุปกรณ์ของบัญชีคุณ</span></div>
        <div className="onboarding-actions">
          <button type="button" className="btn ghost onboarding-skip" disabled={busy} onClick={() => void skip()}>ข้ามไปก่อน</button>
          <button className="btn primary onboarding-submit" disabled={busy}>{busy ? 'กำลังบันทึก…' : 'บันทึกและเริ่มใช้งาน'} <ArrowRight size={17} /></button>
        </div>
      </form>
    </section>
  </main>;
}
